import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { devLog } from "@/lib/log";
import { useApp } from "@/contexts/AppContext";
import { computeGstFromInclusive, gstAutoRatioLabel, getGstStamp } from "@/tax/gst";
import { toast } from "sonner";
import { formatDateShort } from "@/shared/format";
import { ArrowLeft, Save, Trash2, Loader2, Lock, Unlock, Calculator, ImageIcon, Wrench, Camera, Briefcase, Percent, Building2, CheckCircle2 } from "lucide-react";
import VoicePlayer from "@/components/VoicePlayer";
import AttachmentsPanel from "@/components/AttachmentsPanel";
import BiSelect from "@/components/BiSelect";
import BiDatePicker from "@/components/BiDatePicker";
import ConfirmDeleteDialog from "@/components/ConfirmDeleteDialog";
import { useFileToken, buildPhotoHref } from "@/hooks/useFileToken";
import { EntryFlags } from "@/components/flags/SmartFlags";

const STATUS_OPTS = [
  { value: "pending", label: "Pending", cls: "border-[#FBBF24]/40 text-[#FBBF24]" },
  { value: "approved", label: "Approved", cls: "border-[#34D399]/40 text-[#34D399]" },
  { value: "rejected", label: "Rejected", cls: "border-[#F87171]/40 text-[#F87171]" },
];

// Fix-Now hints — one per supported flag key. Drives the amber banner at
// the top of the editor when the tradie arrives via /entries/:id/edit?fix=KEY
// from a filtered ledger jump. `scroll` maps to the ref registered below.
const FIX_HINTS = {
  no_receipt: {
    label: "Missing receipt",
    icon: Camera,
    scroll: "attachments",
    body: "Add a photo or PDF of this receipt below — anything ≥ $82.50 needs evidence for a GST claim.",
  },
  missing_abn_subcontractor: {
    label: "Subcontractor missing ABN",
    icon: Building2,
    scroll: "supplier",
    body: "Add the subbie's ABN below — updates every entry from this supplier.",
  },
  gst_mismatch: {
    label: "GST doesn't equal Total ÷ 11",
    icon: Percent,
    scroll: "gst",
    body: "Unlock GST and tap the recalculate button, or type the correct amount from the supplier's invoice.",
  },
  unassigned_job: {
    label: "Not linked to a job",
    icon: Briefcase,
    scroll: "job",
    body: "Pick the job this expense belongs to so per-site profitability stays accurate.",
  },
};

// Format an ISO-8601 stamp for the audit footnote. Falls back to the raw
// string if the value can't be parsed — never throws on malformed input so
// the audit line degrades gracefully instead of crashing the edit page.
function formatStampedAt(iso) {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

/**
 * Dedicated per-entry edit page. Route: /entries/:id/edit.
 * Ledger rows deep-link here so a tradie can tweak a saved entry without
 * scrolling the noisy Review orchestrator. Purely PUT /api/entries/:id backed.
 */
export default function EntryEdit() {
  const { id } = useParams();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const fixKey = searchParams.get("fix") || "";
  const fixHint = FIX_HINTS[fixKey] || null;
  const { jobs, categories } = useApp();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const fileToken = useFileToken();
  // Supplier ABN (TPAR workflow) — kept in local state and upserted to
  // supplier_twins on save. Prefills from the twin lookup once the entry
  // loads. Blank means "no change" so we never accidentally wipe a
  // previously-stored ABN by clearing the field.
  const [supplierAbn, setSupplierAbn] = useState("");
  const [supplierAbnLoaded, setSupplierAbnLoaded] = useState("");

  // Fix-Now target refs — the correct field for the flag gets a temporary
  // amber ring + scrollIntoView once the entry loads. Refs default to null
  // so a fix param for a field we don't render (rare) is a silent no-op.
  const fixRefs = {
    supplier: useRef(null),
    gst: useRef(null),
    job: useRef(null),
    attachments: useRef(null),
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/entries/${id}`);
      setForm(data);
    } catch (err) {
      devLog.warn("load entry failed", err);
      toast.error("Could not load this entry");
      nav("/history", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [id, nav]);
  useEffect(() => { load(); }, [load]);

  // Supplier ABN prefill (TPAR workflow) — once the entry loads, look up
  // any stored ABN for this supplier so the tradie can see + edit it.
  // Runs only for subcontractor entries; other deduction categories skip
  // the extra network call. Guarded by supplier length so mid-typing
  // requests don't fire.
  useEffect(() => {
    if (!form || form.deduction_category !== "subcontractor") return;
    const supplier = (form.supplier || "").trim();
    if (!supplier) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/suppliers/twin", { params: { supplier } });
        if (cancelled) return;
        const abn = (data?.abn || "").toString();
        setSupplierAbn(abn);
        setSupplierAbnLoaded(abn);
      } catch (err) {
        devLog.debug("supplier twin lookup failed", err);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form?.id, form?.supplier, form?.deduction_category]);

  // Fix-Now — once the entry loads, scroll the target field into view and
  // pulse an amber ring for ~2.5s so the tradie's eye lands on it. Kept
  // in a small state flag so the ring class is clean CSS + testable.
  const [ringOn, setRingOn] = useState(false);
  useEffect(() => {
    if (!form || !fixHint) return;
    const ref = fixRefs[fixHint.scroll];
    if (ref?.current?.scrollIntoView) {
      setTimeout(() => {
        try {
          ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
        } catch { /* older browsers just skip */ }
      }, 150);
    }
    setRingOn(true);
    const t = setTimeout(() => setRingOn(false), 2500);
    return () => clearTimeout(t);
    // We only want this to fire once per (entry, fixKey) combination.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form?.id, fixKey]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const recalcGst = () => {
    if (!form) return;
    // GST rate sourced from the 2026.1 ATO Knowledge Base; helper falls
    // back to the historical 10% if the KB is ever unavailable.
    const gst = computeGstFromInclusive(Number(form.total || 0));
    update({ gst, gst_locked: false });
  };

  const save = async () => {
    if (!form) return;
    setSaving(true);
    try {
      // Upsert supplier ABN before saving the entry so the
      // `missing_abn_subcontractor` flag drops off on the next load /
      // pack regen. Only fires for subcontractor rows AND when the
      // tradie actually typed digits — blanking the field never wipes
      // the stored ABN (contract).
      const abnDigits = (supplierAbn || "").replace(/\D/g, "");
      const supplierClean = (form.supplier || "").trim();
      if (
        form.deduction_category === "subcontractor"
        && supplierClean
        && abnDigits
        && abnDigits !== supplierAbnLoaded.replace(/\D/g, "")
      ) {
        try {
          await api.post("/suppliers/twin", {
            supplier: supplierClean,
            abn: abnDigits,
          });
        } catch (err) {
          devLog.warn("supplier twin upsert failed", err);
          toast.error("Could not save the supplier ABN — entry still saved");
        }
      }
      // Re-stamp on every edit — the tradie may have re-computed GST
      // against the currently active ruleset. The backend stores the
      // latest stamp, so the audit trail always reflects the ruleset
      // that produced the CURRENT gst value on the row.
      const stamp = getGstStamp();
      const payload = {
        job_id: form.job_id || null,
        supplier: form.supplier || "",
        description: form.description || "",
        total: Number(form.total || 0),
        gst: Number(form.gst || 0),
        gst_locked: !!form.gst_locked,
        category: form.category || "",
        cost_code: form.cost_code || "",
        notes: form.notes || "",
        deduction_category: form.deduction_category || "materials",
        entry_date: form.entry_date || null,
        status: form.status || "pending",
        ato_ruleset_version: stamp.version,
        ato_ruleset_stamped_at: stamp.stampedAt,
      };
      await api.put(`/entries/${id}`, payload);
      toast.success("Entry saved");
      nav("/history");
    } catch (err) {
      devLog.warn("save entry failed", err);
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const del = () => setConfirmDeleteOpen(true);
  const confirmDelete = async () => {
    setConfirmDeleteOpen(false);
    try {
      await api.delete(`/entries/${id}`);
      toast.success("Entry deleted");
      nav("/history");
    } catch {
      toast.error("Delete failed");
    }
  };

  const categoryOptions = useMemo(
    () => (Array.isArray(categories) ? categories : []),
    [categories],
  );

  if (loading || !form) {
    return (
      <div className="space-y-4">
        <div className="bi-surface p-8 text-center text-white/50 inline-flex items-center gap-2 justify-center w-full">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading entry…
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-32" data-testid="entry-edit-page">
      <button
        type="button"
        onClick={() => nav(-1)}
        data-testid="entry-edit-back"
        className="inline-flex items-center gap-1 text-white/60 hover:text-white text-sm"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div>
        <h1 className="font-heading text-2xl font-extrabold">Edit entry</h1>
        <div className="text-xs text-white/50 truncate">
          {form.supplier || "Unknown supplier"} · {form.entry_date ? formatDateShort(form.entry_date) : "no date"}
        </div>
      </div>

      {/* Fix-Now banner — Assistant Deep Actions land here with ?fix=KEY.
          Shows the flag label and the exact field to update. Auto-scrolls
          + rings that field for ~2.5s so the tradie can't miss it. */}
      {fixHint && (
        <div
          data-testid={`entry-edit-fix-banner-${fixKey}`}
          className="bi-surface p-3 border border-[#FBBF24]/40 bg-[#FBBF24]/[0.07] flex items-start gap-2"
        >
          <div className="w-8 h-8 rounded-lg bg-[#FBBF24]/15 border border-[#FBBF24]/30 flex items-center justify-center shrink-0">
            <fixHint.icon className="w-4 h-4 text-[#FBBF24]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#FBBF24] font-heading font-bold inline-flex items-center gap-1">
              <Wrench className="w-3 h-3" /> Fix now · {fixHint.label}
            </div>
            <div className="text-[11px] text-white/70 leading-snug mt-0.5">
              {fixHint.body}
            </div>
          </div>
        </div>
      )}

      {/* Smart-flag panel — surfaces every open flag on this entry with a
          dismiss action. Advisory; doesn't gate save. */}
      <EntryFlags entryId={id} className="pt-1" />

      {form.photo_path && (
        <a
          data-testid="entry-edit-photo"
          href={buildPhotoHref({
            path: form.photo_path,
            token: fileToken,
            supplier: form.supplier || "",
            name: form.entry_date || "",
          })}
          target="_blank"
          rel="noopener noreferrer"
          className="block rounded-xl overflow-hidden bg-black/40 border border-white/5 max-h-56"
        >
          <img
            src={`${API_BASE}/api/files/${form.photo_path}`}
            alt="receipt"
            className="w-full h-full object-cover"
          />
        </a>
      )}
      {form.voice_path && (
        <VoicePlayer
          voicePath={form.voice_path}
          transcript={form.raw_transcript}
          testid="entry-edit-voice"
        />
      )}

      <div className="bi-surface p-4 space-y-4">
        <div
          ref={fixRefs.supplier}
          data-testid="entry-edit-supplier-field"
          className={`rounded-lg transition ${fixKey === "missing_abn_subcontractor" && ringOn ? "ring-2 ring-[#FBBF24]/70 ring-offset-2 ring-offset-[#111827] p-2 -m-2" : ""}`}
        >
          <label className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-heading">
            Supplier
          </label>
          <input
            data-testid="entry-edit-supplier"
            value={form.supplier || ""}
            onChange={(e) => update({ supplier: e.target.value })}
            className="bi-input w-full mt-1"
          />
          {/* Subcontractor ABN — inline, advisory. Prefilled from the
              twin; upserted on save so every entry from this supplier
              gets the ABN and the TPAR flag clears. */}
          {form.deduction_category === "subcontractor" && (() => {
            const digits = (supplierAbn || "").replace(/\D/g, "");
            const invalid = digits.length > 0 && digits.length !== 11;
            return (
              <div className="mt-3" data-testid="entry-edit-abn-block">
                <label className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-heading">
                  Subcontractor ABN (advisory)
                </label>
                <input
                  data-testid="entry-edit-supplier-abn"
                  value={supplierAbn}
                  onChange={(e) => setSupplierAbn(e.target.value)}
                  placeholder="11-digit ABN"
                  inputMode="numeric"
                  autoComplete="off"
                  className="bi-input w-full mt-1 tabular-nums"
                />
                <div
                  data-testid="entry-edit-supplier-abn-hint"
                  className={`text-[11px] mt-1 leading-snug ${invalid ? "text-[#FBBF24]" : "text-white/50"}`}
                >
                  {invalid
                    ? `${digits.length}/11 digits — double-check the supplier's invoice.`
                    : "Helper only — confirm with the supplier's invoice. Saved against the supplier so TPAR captures it everywhere."}
                </div>
              </div>
            );
          })()}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-heading">
              Total ($)
            </label>
            <input
              data-testid="entry-edit-total"
              type="number"
              step="0.01"
              min="0"
              inputMode="decimal"
              value={form.total ?? ""}
              onChange={(e) => update({ total: e.target.value })}
              className="bi-input w-full mt-1 tabular-nums"
            />
          </div>
          <div
            ref={fixRefs.gst}
            data-testid="entry-edit-gst-field"
            className={`rounded-lg transition ${fixKey === "gst_mismatch" && ringOn ? "ring-2 ring-[#FBBF24]/70 ring-offset-2 ring-offset-[#111827] p-2 -m-2" : ""}`}
          >
            <div className="flex items-center justify-between">
              <label className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-heading">
                GST ($)
              </label>
              <button
                type="button"
                data-testid="entry-edit-gst-lock"
                onClick={() => update({ gst_locked: !form.gst_locked })}
                className="text-white/60 hover:text-[#FF9500]"
                title={form.gst_locked ? "Unlock GST" : "Lock GST"}
              >
                {form.gst_locked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
              </button>
            </div>
            <div className="mt-1 flex gap-1.5">
              <input
                data-testid="entry-edit-gst"
                type="number"
                step="0.01"
                min="0"
                inputMode="decimal"
                value={form.gst ?? ""}
                onChange={(e) => update({ gst: e.target.value })}
                className="bi-input flex-1 tabular-nums"
              />
              <button
                type="button"
                data-testid="entry-edit-gst-recalc"
                onClick={recalcGst}
                className="w-9 rounded-lg bi-ghost-btn flex items-center justify-center text-[#FF9500]"
                title={`Recalculate ${gstAutoRatioLabel()}`}
              >
                <Calculator className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-heading">
            Date
          </label>
          <BiDatePicker
            data-testid="entry-edit-date"
            value={form.entry_date || ""}
            onChange={(e) => update({ entry_date: e.target.value })}
            className="bi-input w-full mt-1"
          />
        </div>

        <div
          ref={fixRefs.job}
          data-testid="entry-edit-job-field"
          className={`rounded-lg transition ${fixKey === "unassigned_job" && ringOn ? "ring-2 ring-[#FBBF24]/70 ring-offset-2 ring-offset-[#111827] p-2 -m-2" : ""}`}
        >
          <label className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-heading">
            Job
          </label>
          <BiSelect
            data-testid="entry-edit-job"
            value={form.job_id || ""}
            onChange={(e) => update({ job_id: e.target.value || null })}
            className="bi-input w-full mt-1"
          >
            <option value="">Unassigned</option>
            {jobs.map((j) => (
              <option key={j.id} value={j.id}>{j.name}</option>
            ))}
          </BiSelect>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-heading">
              Category
            </label>
            <BiSelect
              data-testid="entry-edit-category"
              value={form.category || ""}
              onChange={(e) => update({ category: e.target.value })}
              className="bi-input w-full mt-1"
            >
              <option value="">—</option>
              {categoryOptions.map((c) => (
                <option key={c.id} value={c.name}>{c.name}</option>
              ))}
            </BiSelect>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-heading">
              Cost code
            </label>
            <input
              data-testid="entry-edit-costcode"
              value={form.cost_code || ""}
              onChange={(e) => update({ cost_code: e.target.value })}
              placeholder="e.g. Timber, Plumbing"
              className="bi-input w-full mt-1"
            />
          </div>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-heading">
            Description
          </label>
          <input
            data-testid="entry-edit-desc"
            value={form.description || ""}
            onChange={(e) => update({ description: e.target.value })}
            className="bi-input w-full mt-1"
          />
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-heading">
            Notes
          </label>
          <textarea
            data-testid="entry-edit-notes"
            value={form.notes || ""}
            onChange={(e) => update({ notes: e.target.value })}
            rows={3}
            className="bi-input w-full mt-1 resize-none"
          />
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-heading mb-1 block">
            Status
          </label>
          <div className="flex flex-wrap items-center gap-2">
            {STATUS_OPTS.map((s) => (
              <button
                key={s.value}
                type="button"
                data-testid={`entry-edit-status-${s.value}`}
                onClick={() => update({ status: s.value })}
                className={`h-9 px-3 rounded-full text-[11px] uppercase font-bold tracking-wider border transition ${
                  form.status === s.value ? `${s.cls} bg-white/[0.06]` : "text-white/40 border-white/10"
                }`}
              >
                {s.label}
              </button>
            ))}
            {form.client_confirmed && (
              <span
                data-testid="entry-edit-confirmed-chip"
                title="You've vouched for the category and GST on this entry."
                className="h-9 px-3 rounded-full text-[11px] uppercase font-bold tracking-wider border border-[#FF9500]/70 text-[#FF9500] bg-[#FF9500]/[0.14] inline-flex items-center gap-1.5"
              >
                <CheckCircle2 className="w-3.5 h-3.5" /> Client confirmed
              </span>
            )}
          </div>
        </div>

        {/* Attachments — always render so the ring target exists AND the
            tradie can add a receipt inline when they arrive via
            /entries/:id/edit?fix=no_receipt. */}
        <div
          ref={fixRefs.attachments}
          className={`pt-2 border-t border-white/5 rounded-lg transition ${fixKey === "no_receipt" && ringOn ? "ring-2 ring-[#FBBF24]/70 ring-offset-2 ring-offset-[#111827] p-2 -m-2" : ""}`}
          data-testid="entry-edit-attachments"
        >
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2 inline-flex items-center gap-1">
            <ImageIcon className="w-3 h-3" /> Attachments
          </div>
          <AttachmentsPanel
            entryId={id}
            attachments={form.attachments || []}
            onChange={(next) => update({ attachments: next })}
            onOcrApplied={(_parsed, _filled, entryPatch) => {
              if (entryPatch) update(entryPatch);
            }}
            testidPrefix="entry-edit-attach"
          />
        </div>

        {/* ATO ruleset audit stamp — subtle one-line provenance footnote so a
            tradie (or their accountant) can see which ruleset produced the GST
            portion on this entry. Rendered only if the backend has the stamp. */}
        {form.ato_ruleset_version && (
          <div
            data-testid="entry-edit-ato-stamp"
            className="pt-3 border-t border-white/5 text-[11px] text-white/40 tabular-nums"
          >
            Calculated under ATO ruleset{" "}
            <span className="text-white/60 font-heading font-bold">
              {form.ato_ruleset_version}
            </span>
            {form.ato_ruleset_stamped_at && (
              <>
                <span className="text-white/25"> · </span>
                stamped{" "}
                <span className="text-white/60">
                  {formatStampedAt(form.ato_ruleset_stamped_at)}
                </span>
              </>
            )}
          </div>
        )}
      </div>

      <div className="fixed bottom-24 left-0 right-0 px-4">
        <div className="max-w-[560px] mx-auto grid grid-cols-[auto_1fr] gap-2">
          <button
            type="button"
            data-testid="entry-edit-delete"
            onClick={del}
            className="h-14 w-14 rounded-xl bi-ghost-btn flex items-center justify-center text-[#F87171]"
            title="Delete"
          >
            <Trash2 className="w-5 h-5" />
          </button>
          <button
            type="button"
            data-testid="entry-edit-save"
            onClick={save}
            disabled={saving}
            className="h-14 rounded-xl bi-accent-btn flex items-center justify-center gap-2 disabled:opacity-60 shadow-[0_10px_30px_-15px_rgba(255,149,0,0.7)]"
          >
            {saving ? (
              <><Loader2 className="w-5 h-5 animate-spin" /> Saving…</>
            ) : (
              <><Save className="w-5 h-5" /> Save changes</>
            )}
          </button>
        </div>
      </div>

      <ConfirmDeleteDialog
        open={confirmDeleteOpen}
        title="Delete this entry?"
        description="This can't be undone. The entry and its attachments will be permanently removed."
        onConfirm={confirmDelete}
        onCancel={() => setConfirmDeleteOpen(false)}
        testId="entry-edit-delete"
      />
    </div>
  );
}
